/**
 * AWS CloudWatch RUM (Real User Monitoring) 統合モジュール
 */

import { AwsRum, AwsRumConfig } from 'aws-rum-web';

// 型定義
interface RUMEnvironmentConfig {
  applicationId: string;
  applicationVersion: string;
  applicationRegion: string;
  identityPoolId: string;
  endpoint: string;
  environment: string;
}

// 環境変数から設定を取得
const getRUMConfig = (): RUMEnvironmentConfig | null => {
  const applicationId = import.meta.env.VITE_AWS_RUM_APPLICATION_ID;
  const identityPoolId = import.meta.env.VITE_AWS_RUM_IDENTITY_POOL_ID;
  const endpoint = import.meta.env.VITE_AWS_RUM_ENDPOINT;

  // 必須設定の確認
  if (!applicationId || !identityPoolId || !endpoint) {
    console.warn('AWS RUM configuration is incomplete');
    return null;
  }

  return {
    applicationId,
    applicationVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
    applicationRegion: import.meta.env.VITE_AWS_REGION || 'ap-northeast-1',
    identityPoolId,
    endpoint,
    environment: import.meta.env.VITE_ENVIRONMENT || 'development'
  };
};


// RUM インスタンス
let rumInstance: AwsRum | null = null;

/**
 * AWS RUM を初期化
 */
export function initializeRUM(): AwsRum | null {
  try {
    // 既に初期化されている場合はスキップ
    if (rumInstance) {
      return rumInstance;
    }

    // 環境設定の取得
    const config = getRUMConfig();
    if (!config) {
      console.log('RUM initialization skipped: configuration not available');
      return null;
    }

    // 本番環境でのみ有効化（オプション）
    if (config.environment !== 'production' && import.meta.env.VITE_RUM_ENABLE_DEV !== 'true') {
      console.log('RUM is disabled in non-production environment');
      return null;
    }

    // RUM設定
    const rumConfig: AwsRumConfig = {
      // sessionSampleRate: config.environment === 'production' ? 0.1 : 1, // 本番では10%サンプリング
      sessionSampleRate: 1, //必ずサンプリング(本番の時は環境変数で変更できるようにするか、本番取得の値を決めておく)
      identityPoolId: config.identityPoolId,
      endpoint: config.endpoint,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
      signing: true,
      // カスタム属性の追加
      customAttributes: {
        environment: config.environment,
        version: config.applicationVersion
      }
    };

    // RUM初期化
    rumInstance = new AwsRum(
      config.applicationId,
      config.applicationVersion,
      config.applicationRegion,
      rumConfig
    );

    // 初期化成功イベントの記録
    rumInstance.recordEvent('app_initialized', {
      version: config.applicationVersion,
      environment: config.environment,
      timestamp: new Date().toISOString()
    });

    // グローバルに参照可能にする
    if (typeof window !== 'undefined') {
      (window as any).awsRum = rumInstance;
    }

    console.log('AWS RUM initialized successfully');
    return rumInstance;
  } catch (error) {
    console.error('AWS RUM initialization failed:', error);
    return null;
  }
}

/**
 * カスタムイベントを記録
 */
export function recordUserAction(action: string, details?: Record<string, any>): void {
  if (!rumInstance) {
    console.debug(`[RUM] event: ${action}`, details ?? '');
    return;
  }

  try {
    rumInstance.recordEvent(action, {
      ...details,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to record user action:', error);
  }
}

/**
 * エラーを記録
 */
export function recordError(error: Error | string, context?: Record<string, any>): void {
  if (!rumInstance) {
    console.debug('[RUM] error:', error, context ?? '');
    return;
  }

  try {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    rumInstance.recordError(errorObj, context);
  } catch (err) {
    console.error('Failed to record error:', err);
  }
}

/**
 * ページビューを記録
 */
export function recordPageView(pageName: string, pageAttributes?: Record<string, any>): void {
  if (!rumInstance) {
    console.debug(`[RUM] pageView: ${pageName}`, pageAttributes ?? '');
    return;
  }

  try {
    rumInstance.recordPageView({
      pageId: pageName,
      ...pageAttributes
    });
  } catch (error) {
    console.error('Failed to record page view:', error);
  }
}

/**
 * パフォーマンスメトリクスを記録
 */
export function recordPerformanceMetric(
  metricName: string,
  value: number,
  unit: 'seconds' | 'milliseconds' | 'bytes' | 'percent' = 'milliseconds'
): void {
  if (!rumInstance) {
    console.debug(`[RUM] metric: ${metricName} = ${value} ${unit}`);
    return;
  }

  try {
    rumInstance.recordEvent('custom_metric', {
      metric_name: metricName,
      value,
      unit,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to record performance metric:', error);
  }
}

/**
 * ユーザーセッション情報を設定
 */
export function setUserInfo(userId: string, attributes?: Record<string, any>): void {
  if (!rumInstance) {
    console.debug('RUM not available, skipping user info setting');
    return;
  }

  try {
    // ユーザーIDの設定
    rumInstance.setCustomAttribute('user_id', userId);

    // 追加属性の設定
    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        rumInstance.setCustomAttribute(key, value);
      });
    }
  } catch (error) {
    console.error('Failed to set user info:', error);
  }
}

/**
 * RUM を無効化
 */
export function disableRUM(): void {
  if (rumInstance) {
    try {
      // RUMの無効化処理（APIが提供されている場合）
      // rumInstance.disable();
      rumInstance = null;
      if (typeof window !== 'undefined') {
        delete (window as any).awsRum;
      }
      console.log('AWS RUM disabled');
    } catch (error) {
      console.error('Failed to disable RUM:', error);
    }
  }
}

/**
 * RUM の状態を取得
 */
export function getRUMStatus(): {
  enabled: boolean;
  sessionId?: string;
  environment?: string;
} {
  if (!rumInstance) {
    return { enabled: false };
  }

  return {
    enabled: true,
    sessionId: rumInstance.getSessionId ? rumInstance.getSessionId() : undefined,
    environment: import.meta.env.VITE_ENVIRONMENT || 'development'
  };
}

// デフォルトエクスポート
export default {
  initialize: initializeRUM,
  recordUserAction,
  recordError,
  recordPageView,
  recordPerformanceMetric,
  setUserInfo,
  disable: disableRUM,
  getStatus: getRUMStatus
};